import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MapProvider } from '@/components/maps/map-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Navbar } from '@/components/layout/navbar';
import { HomePage } from '@/pages/home';
import { AboutPage } from '@/pages/about';
import { ServicesPage } from '@/pages/services';
import { ContactPage } from '@/pages/contact';
import { LoginPage } from '@/pages/auth/login';
import { RegisterPage } from '@/pages/auth/register';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { ChatPage } from '@/pages/chat';
import { ServiceRequestsPage } from '@/pages/owner/service-requests';
import { TaskManagementPage } from '@/pages/owner/service-requests/tasks';
import { CustomerPortalPage } from '@/pages/owner/customer-portal';
import { PropertyOwnerDashboard } from '@/pages/owner/dashboard';
import { ProfilePage } from '@/pages/profile';
import { SettingsPage } from '@/pages/settings';
import { NotificationsPage } from '@/pages/notifications';
import { RAGDebugger } from '@/components/ai/rag-debugger';

// Admin Pages
import { AdminDashboard } from '@/pages/admin/dashboard';
import { UsersPage } from '@/pages/admin/users';
import { AuditPage } from '@/pages/admin/audit';
import { ApiKeysPage } from '@/pages/admin/api-keys';
import AdminContractsPage from '@/pages/admin/contracts';

// Property Manager Pages
import { PropertyManagerDashboard } from '@/pages/property-manager/dashboard';
import { PropertiesPage } from '@/pages/property-manager/properties';
import { HelpersPage } from '@/pages/property-manager/helpers';

// FlexHelper Pages
import { HelperDashboard } from '@/pages/helper/dashboard';
import { HelperLocationPage } from '@/pages/helper/location';
import { HelperFeedbackPage } from '@/pages/helper/feedback';

import { Toaster } from '@/components/ui/toaster';
import { AppModeProvider } from '@/hooks/use-app-mode';
import { FlexHelpAIPage } from '@/pages/flexhelp-ai';
import { useState, useEffect, Component, ErrorInfo, ReactNode } from 'react';
import { errorCapture } from './debug-utils';

// Error boundary component to catch rendering errors
class ErrorBoundary extends Component<{ children: ReactNode, fallback?: ReactNode }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: ReactNode, fallback?: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to our error capture service
    errorCapture.captureError(`Error boundary caught error: ${error.message}`, error.stack);
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback || (
        <div className="error-fallback">
          <h2>Something went wrong</h2>
          <p>The application encountered an error while rendering this component.</p>
          <p><strong>Error:</strong> {this.state.error?.message}</p>
          <button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="error-fallback-button"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Production environment check
const isProduction = process.env.NODE_ENV === 'production';

// Helper function to detect Netlify environment
const isNetlifyEnvironment = () => {
  return typeof window !== 'undefined' && 
    (window.location.hostname.includes('netlify.app') || 
     document.referrer.includes('netlify.app'));
};

// Monitor and hide runtime errors related to WebSockets
const setupRuntimeErrorHandling = () => {
  // Target the error container that shows up in the UI
  const hideRuntimeErrorContainer = () => {
    // Use a mutation observer to detect and hide the runtime errors container
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const errorContainer = document.querySelector('div[id*="runtime"], div[id*="error"], div.error-container');
          if (errorContainer && errorContainer instanceof HTMLElement) {
            // In production, hide WebSocket-related error messages
            if (isProduction) {
              errorContainer.style.display = 'none';
            } else {
              // In development, make it less intrusive but still visible
              errorContainer.style.opacity = '0.4';
              errorContainer.style.maxHeight = '30vh';
              errorContainer.style.overflow = 'auto';
            }
          }
        }
      });
    });

    // Start observing the document body for error containers
    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
  };

  return hideRuntimeErrorContainer();
};

export function App() {
  // Only show debug information in development mode
  const [isDebugVisible, setIsDebugVisible] = useState(!isProduction);
  const [appVersion] = useState(() => {
    // Generate a unique version ID for this session to help with debugging
    return `${new Date().toISOString().split('T')[0]}-${Math.random().toString(36).substring(2, 8)}`;
  });
  
  // Setup error handling and app initialization
  useEffect(() => {
    // Only log version in development
    if (!isProduction) {
      console.log(`[FlexHelp] App version: ${appVersion}`);
    }
    
    // Custom error handler to filter WebSocket errors in production
    const handleError = (event: ErrorEvent) => {
      // In production, prevent WebSocket errors from being reported/displayed
      if (isProduction && (
          event.message.includes('WebSocket') ||
          event.message.includes('socket') ||
          event.message.includes('net::') ||
          event.message.includes('connection')
        )) {
        event.preventDefault();
        return false;
      }
      
      // Log other errors
      errorCapture.captureError(`Global error: ${event.message}`, event.error?.stack);
      return true;
    };
    
    // Set up error handling
    window.addEventListener('error', handleError, true);
    
    // Setup runtime error UI handling
    const observer = setupRuntimeErrorHandling();
    
    // In production, hide the "Loading FlexHelp..." message that appears
    if (isProduction) {
      // Create a style element to hide the loading message
      const style = document.createElement('style');
      style.textContent = `
        /* Hide the loading message in production */
        body > div:not([class]):not([id]) {
          display: none !important;
        }
        /* Hide any error messages with runtime in the ID or text */
        div[id*="runtime"],
        div[id*="error"],
        div.runtime-error,
        div.error-container,
        div.error-overlay {
          display: none !important;
        }
      `;
      document.head.appendChild(style);

      // Also try to find and hide the loading message directly
      setTimeout(() => {
        try {
          // Find all elements that might be loading indicators
          const allElements = document.querySelectorAll('div, span, p');
          allElements.forEach(el => {
            const element = el as HTMLElement;
            // Check if the element contains loading text
            if (element.textContent && 
                (element.textContent.toLowerCase().includes('loading') || 
                 element.textContent.toLowerCase().includes('please wait'))) {
              element.style.display = 'none';
              element.style.opacity = '0';
              element.style.visibility = 'hidden';
            }
            
            // Also hide elements with loading-related classes or IDs
            const elId = element.id || '';
            const elClass = element.className || '';
            if (elId.toLowerCase().includes('loading') || 
                elId.toLowerCase().includes('spinner') || 
                elClass.toLowerCase().includes('loading') || 
                elClass.toLowerCase().includes('spinner')) {
              element.style.display = 'none';
              element.style.opacity = '0';
              element.style.visibility = 'hidden';
            }
          });
          
          // Specifically target the loading indicator from index.html
          const loadingIndicator = document.getElementById('loading-indicator');
          if (loadingIndicator) {
            loadingIndicator.style.display = 'none';
            loadingIndicator.style.opacity = '0';
            loadingIndicator.style.visibility = 'hidden';
          }
        } catch (err) {
          console.error('Error hiding loading elements:', err);
        }
      }, 500);
      
      // Add a failsafe timeout to ensure loading screen disappears
      // This is especially important for Netlify deployments
      const forceHideLoadingScreen = () => {
        // Force hide any loading indicators that might be stuck
        const possibleLoadingElements = [
          // Generic loading elements
          'div:not([class]):not([id])',
          // Loading spinners and overlays
          '.loading-spinner',
          '.loading-overlay',
          '.loading-container',
          // Error overlays that might prevent app rendering
          '.error-overlay',
          // Elements with loading in their ID or class
          '[id*="loading"]',
          '[class*="loading"]'
        ];
        
        // Create a more aggressive style to hide loading elements and error displays
        const forceHideStyle = document.createElement('style');
        forceHideStyle.textContent = `
          /* Force hide any loading elements */
          .loading-spinner, 
          .loading-overlay, 
          .loading-container,
          .error-overlay,
          div[class*="loading"],
          div[id*="loading"],
          #loading-indicator {
            display: none !important;
            opacity: 0 !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
          
          /* Hide runtime error panels in production */
          div[id*="runtime"],
          div[class*="runtime"],
          div[id*="error"],
          div[class*="error"],
          div[role="alert"],
          [data-testid*="error"] {
            display: none !important;
            opacity: 0 !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
          
          /* Ensure the app content is visible */
          #root, 
          .app-container, 
          body > div[id] {
            display: block !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
        `;
        document.head.appendChild(forceHideStyle);
        
        // Also try direct DOM manipulation for elements that might be stuck
        document.querySelectorAll(possibleLoadingElements.join(', ')).forEach(el => {
          if (el instanceof HTMLElement) {
            el.style.display = 'none';
            el.style.opacity = '0';
            el.style.visibility = 'hidden';
          }
        });
        
        // Make sure the root element is visible
        const rootElement = document.getElementById('root');
        if (rootElement) {
          rootElement.style.display = 'block';
          rootElement.style.opacity = '1';
          rootElement.style.visibility = 'visible';
        }
      };
      
      // Apply the failsafe after a reasonable timeout
      // First attempt - quick timeout for fast connections
      setTimeout(forceHideLoadingScreen, 2000);
      // Second attempt - longer timeout as a backup
      setTimeout(forceHideLoadingScreen, 5000);
    }
    
    return () => {
      window.removeEventListener('error', handleError);
      observer.disconnect();
    };
  }, [appVersion]);
  
  // Hide debug overlay after 5 seconds in development, immediately in production
  useEffect(() => {
    if (isProduction) {
      setIsDebugVisible(false);
    } else {
      const timer = setTimeout(() => setIsDebugVisible(false), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <ErrorBoundary>
      {isDebugVisible && (
        <div className="debug-overlay">
          App component mounted. Version: {appVersion} | Rendering routes...
          <button 
            onClick={() => setIsDebugVisible(false)}
            className="debug-close-button"
          >
            Close
          </button>
        </div>
      )}
      <Router>
        <AppModeProvider>
          <TooltipProvider>
            <MapProvider>
              <ErrorBoundary>
                <div className="relative flex min-h-screen flex-col">
                  <Routes>
                    {/* Public routes */}
                    <Route
                      path="/"
                      element={
                        <ErrorBoundary>
                          <>
                            <Navbar />
                            <HomePage />
                          </>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/about"
                      element={
                        <ErrorBoundary>
                          <>
                            <Navbar />
                            <AboutPage />
                          </>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/services"
                      element={
                        <ErrorBoundary>
                          <>
                            <Navbar />
                            <ServicesPage />
                          </>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="/contact"
                      element={
                        <ErrorBoundary>
                          <>
                            <Navbar />
                            <ContactPage />
                          </>
                        </ErrorBoundary>
                      }
                    />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />

                    {/* Protected routes */}
                    <Route
                      path="/profile"
                      element={
                        <ErrorBoundary>
                          <ProtectedRoute>
                            <DashboardLayout>
                              <ProfilePage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        </ErrorBoundary>
                      }
                    />

                    <Route
                      path="/settings"
                      element={
                        <ErrorBoundary>
                          <ProtectedRoute>
                            <DashboardLayout>
                              <SettingsPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        </ErrorBoundary>
                      }
                    />

                    <Route
                      path="/notifications"
                      element={
                        <ErrorBoundary>
                          <ProtectedRoute>
                            <DashboardLayout>
                              <NotificationsPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        </ErrorBoundary>
                      }
                    />

                    <Route
                      path="/chat"
                      element={
                        <ProtectedRoute>
                          <DashboardLayout>
                            <ChatPage />
                          </DashboardLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/flexhelp-ai"
                      element={
                        <ProtectedRoute>
                          <DashboardLayout>
                            <FlexHelpAIPage />
                          </DashboardLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin routes */}
                    <Route
                      path="/admin/*"
                      element={
                        <ErrorBoundary>
                          <ProtectedRoute allowedRoles={['admin']}>
                            <DashboardLayout>
                              <Routes>
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="dashboard" element={<AdminDashboard />} />
                                <Route path="users" element={<UsersPage />} />
                                <Route path="settings" element={<SettingsPage />} />
                                <Route path="audit" element={<AuditPage />} />
                                <Route path="api-keys" element={<ApiKeysPage />} />
                                <Route path="contracts" element={<AdminContractsPage />} />
                              </Routes>
                            </DashboardLayout>
                          </ProtectedRoute>
                        </ErrorBoundary>
                      }
                    />

                    {/* Property Manager routes */}
                    <Route
                      path="/manager/*"
                      element={
                        <ErrorBoundary>
                          <ProtectedRoute allowedRoles={['property_manager']}>
                            <DashboardLayout>
                              <Routes>
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="dashboard" element={<PropertyManagerDashboard />} />
                                <Route path="service-requests" element={<ServiceRequestsPage />} />
                                <Route path="service-requests/tasks" element={<TaskManagementPage />} />
                                <Route path="properties" element={<PropertiesPage />} />
                                <Route path="helpers" element={<HelpersPage />} />
                              </Routes>
                            </DashboardLayout>
                          </ProtectedRoute>
                        </ErrorBoundary>
                      }
                    />

                    {/* Property Owner routes */}
                    <Route
                      path="/owner/*"
                      element={
                        <ErrorBoundary>
                          <ProtectedRoute allowedRoles={['property_owner']}>
                            <DashboardLayout>
                              <Routes>
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="dashboard" element={<PropertyOwnerDashboard />} />
                                <Route path="service-requests" element={<ServiceRequestsPage />} />
                                <Route path="service-requests/tasks" element={<TaskManagementPage />} />
                                <Route path="customer-portal" element={<CustomerPortalPage />} />
                                <Route path="feedback" element={<HelperFeedbackPage />} />
                              </Routes>
                            </DashboardLayout>
                          </ProtectedRoute>
                        </ErrorBoundary>
                      }
                    />

                    {/* FlexHelper routes */}
                    <Route
                      path="/helper/*"
                      element={
                        <ErrorBoundary>
                          <ProtectedRoute allowedRoles={['flex_helper']}>
                            <DashboardLayout>
                              <Routes>
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="dashboard" element={<HelperDashboard />} />
                                <Route path="location" element={<HelperLocationPage />} />
                                <Route path="feedback" element={<HelperFeedbackPage />} />
                              </Routes>
                            </DashboardLayout>
                          </ProtectedRoute>
                        </ErrorBoundary>
                      }
                    />

                    <Route path="/rag-debug" element={<RAGDebugger />} />

                    {/* Fallback route */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </div>
              </ErrorBoundary>
              <Toaster />
            </MapProvider>
          </TooltipProvider>
        </AppModeProvider>
      </Router>
    </ErrorBoundary>
  );
}